import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { analysisApi, promptApi } from '../services/api';
import { AnalysisResult, BrandSafetyPrompt, PaginationInfo } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useSelectedBlueprint } from '../hooks/useSelectedBlueprint';
import { usePortalSlot } from '../hooks/usePortalSlot';
import { formatApiErrorMessage } from '../utils/errorMessageUtils';
import FeedbackButtons from './FeedbackButtons';
import Pagination from './ui/pagination';
import AnalysisProfileSelect from './AnalysisProfileSelect';
import MarketPill, { MarketOption } from './MarketPill';
import BulkJobExport from './BulkJobExport';
import PromptExport from './PromptExport';
import { Tabs } from './ui/tabs';
import { Button } from './ui/button';
import { Modal } from './ui/modal';
import { Progress } from './ui/progress';
import { StatCard } from './ui/StatCard';
import { TableFillBox, tableFillHeightClass } from './ui/tableFillBox';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from './ui/table';

// Format large numbers to max 4 characters
const formatCompactNumber = (num: number): string => {
  if (num < 1000) return num.toString();
  if (num < 10000) return (num / 1000).toFixed(1) + 'K';
  if (num < 1000000) return Math.round(num / 1000) + 'K';
  if (num < 10000000) return (num / 1000000).toFixed(1) + 'M';
  if (num < 1000000000) return Math.round(num / 1000000) + 'M';
  if (num < 10000000000) return (num / 1000000000).toFixed(1) + 'B';
  return Math.round(num / 1000000000) + 'B';
};

// Retry utility for API calls with exponential backoff
const withRetry = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 2000
): Promise<T> => {
  let lastError: any;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      const errorMessage = error?.response?.data?.error || error?.message || '';
      // Only retry on timeout/connection errors
      const isRetryable = errorMessage.includes('timed out') ||
                          errorMessage.includes('ECONNRESET') ||
                          errorMessage.includes('socket hang up') ||
                          error?.code === 'ECONNABORTED';
      if (!isRetryable || attempt === maxRetries - 1) {
        throw error;
      }
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`🔄 Retry ${attempt + 1}/${maxRetries} after ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};

// Whether the AI verdict and the keyword-blocking verdict agree — null when
// no keyword list was selected (keyword blocking didn't run for this row).
type Agreement = 'agree' | 'differ' | null;
const NO_KEYWORD_LIST_REASONING = 'No keyword list selected. Keyword blocking disabled for this analysis.';
const getAgreement = (
  analysis: Pick<AnalysisResult, 'isBrandSafe' | 'keywordBlocking'>,
  promptType: 'brand-safety' | 'audience-targeting'
): Agreement => {
  if (!analysis.keywordBlocking || analysis.keywordBlocking.reasoning === NO_KEYWORD_LIST_REASONING) {
    return null;
  }
  const aiPositive = promptType === 'brand-safety' ? !analysis.isBrandSafe : analysis.isBrandSafe;
  const keywordPositive = analysis.keywordBlocking.isBlocked;
  return aiPositive === keywordPositive ? 'agree' : 'differ';
};

function AgreementPill({ agreement }: { agreement: Agreement }) {
  if (agreement === null) {
    return <span className="text-ink-muted">—</span>;
  }
  if (agreement === 'agree') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-safe-bg text-safe">
        Agree
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-warn-bg text-warn">
      ⚠ Differ
    </span>
  );
}

function VerdictPill({ isBrandSafe, label, size = 'sm' }: { isBrandSafe: boolean; label: string; size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full font-bold uppercase ${sizeClasses} ${
      isBrandSafe ? 'bg-safe-bg text-safe' : 'bg-risk-bg text-risk'
    }`}>
      {label}
    </span>
  );
}


const AnalysisHistory: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { selectedCompanyId } = useCompany();
  // eskimi_admin/adops have no company of their own — history/summary need
  // the selected company as an explicit scope, same as promptApi.getAll
  // (bd deepcontext-jvwh.3).
  const scopeCompanyId = (user?.role === 'eskimi_admin' || user?.role === 'adops')
    ? selectedCompanyId || undefined
    : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const [analyses, setAnalyses] = useState<AnalysisResult[]>([]);
  // The selected blueprint, resolved by id via promptApi.getById — not a
  // preloaded list. AnalysisProfileSelect runs its own server-side search
  // for the picker UI; this page only needs the single currently-selected
  // blueprint's details (name/type) for the table, export and deep-link
  // validation (bd deepcontext-q2bz.1).
  const [selectedPrompt, setSelectedPrompt] = useState<BrandSafetyPrompt | null>(null);
  // Fallback name/type lookup for rows whose promptId doesn't (yet) match
  // `selectedPrompt` — e.g. mid blueprint-switch, a stale row from the
  // previous selection can render for a beat before its request lands, and
  // the "All blueprints" state (no filter) can show rows spanning many
  // blueprints. Populated lazily by id, never by a capped preload
  // (bd deepcontext-q2bz.1).
  const [promptDetailsCache, setPromptDetailsCache] = useState<Record<string, BrandSafetyPrompt>>({});
  const requestedPromptIds = useRef<Set<string>>(new Set());
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AnalysisResult | null>(null);
  const [filter, setFilter] = useState<'all' | 'safe' | 'unsafe'>('all');
  // Persisted blueprint selection (shared across pages via localStorage).
  const [selectedBlueprintId, setSelectedBlueprintId, clearSelectedBlueprint] = useSelectedBlueprint();
  // Seed the filter from the URL if present (deep-link), otherwise fall back to
  // the persisted blueprint selection so the choice survives navigation.
  const [promptFilter, setPromptFilter] = useState<string>(
    () => searchParams.get('brand_blueprint_id') || selectedBlueprintId || ''
  );
  // Market pill (bd deepcontext-z5mx.2): per-market result counts for the
  // selected blueprint, and the market key currently narrowing the table/
  // tabs/KPI tiles ('' = no filter / "All markets"). Seeded from the URL so
  // a shared /history?...&market=ZA link reproduces the same narrowed view.
  const [markets, setMarkets] = useState<MarketOption[]>([]);
  const [marketFilter, setMarketFilter] = useState<string>(
    () => searchParams.get('market') || ''
  );
  const marketsAbortController = useRef<AbortController | null>(null);
  const isInitialMarketEffect = useRef(true);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  // deepcontext-6i0j.5 — the tabs + Export actions row shares AppLayout's
  // header line via the app-page-actions slot, mirroring ScheduledJobsPage.tsx.
  // The Brand Blueprint select is captioned and first on the row, mounted
  // separately into the left context slot instead (deepcontext-tpe1).
  const actionsSlot = usePortalSlot('app-page-actions');
  const contextSlot = usePortalSlot('app-page-context');

  // Single entry point for changing the blueprint filter: keep local state and
  // the persisted selection in sync. Empty string is a valid "All blueprints"
  // state, so it's written through too.
  const handlePromptFilterChange = (id: string) => {
    setPromptFilter(id);
    setSelectedBlueprintId(id);
  };

  // The selected blueprint no longer resolves (deleted/inaccessible) — clear the
  // persisted selection and reset the filter to All blueprints.
  const handleInvalidSelection = () => {
    clearSelectedBlueprint();
    setPromptFilter('');
  };
  const [counts, setCounts] = useState<{safe: number; unsafe: number; total: number} | null>(null);
  const [analysisSummary, setAnalysisSummary] = useState<{
    totalUrls: number;
    matchedUrls: number;
    unmatchedUrls: number;
    matchRate: number;
    averageConfidence: number;
  } | null>(null);
  const [dailyAvails, setDailyAvails] = useState<number | null>(null);
  const [dailyAvailsUnavailable, setDailyAvailsUnavailable] = useState(false);
  const [dailyAvailsLoading, setDailyAvailsLoading] = useState(false);
  const [urlDailyAvails, setUrlDailyAvails] = useState<Record<string, number>>({});
  const [urlDailyAvailsLoading, setUrlDailyAvailsLoading] = useState(false);
  const [bidderSyncStatus, setBidderSyncStatus] = useState<{
    syncedCount: number;
    notSyncedCount: number;
    failedCount: number;
    totalMatchedUrls: number;
    syncPercentage: number;
  } | null>(null);
  const [bidderSyncLoading, setBidderSyncLoading] = useState(false);
  const [analysisSummaryLoading, setAnalysisSummaryLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dataSource, setDataSource] = useState<'mysql' | 'clickhouse'>('mysql');

  // Result cache
  const resultCache = useRef<Map<string, {
    data: AnalysisResult[];
    pagination: PaginationInfo;
    counts: {safe: number; unsafe: number; total: number} | null;
    timestamp: number;
    dataSource?: 'mysql' | 'clickhouse';
  }>>(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Debouncing
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Track current summary request to handle race conditions
  const summaryAbortController = useRef<AbortController | null>(null);

  // Track current loadAnalyses request to cancel stale in-flight requests when
  // the user switches blueprint/filter (otherwise a slow A response can land
  // after B and overwrite B's data — bd deepcontext-5jt).
  const analysesAbortController = useRef<AbortController | null>(null);

  // Track the current getById request for the selected blueprint so a
  // stale out-of-order response (e.g. rapid blueprint switches) can't
  // overwrite a newer selection's details.
  const selectedPromptRequestId = useRef(0);

  // Resolve the selected blueprint's details by id whenever promptFilter
  // changes. The picker (AnalysisProfileSelect) searches server-side and
  // never preloads a capped list, so this page can no longer look the
  // selection up in a local array — it must fetch the one blueprint it
  // needs directly (bd deepcontext-q2bz.1). Doubles as deep-link
  // validation: a 404 clears the stale selection via handleInvalidSelection.
  useEffect(() => {
    if (!promptFilter) {
      setSelectedPrompt(null);
      return;
    }

    const requiresCompanySelection = user?.role === 'eskimi_admin' || user?.role === 'adops';
    // CompanyContext resolves selectedCompanyId asynchronously, so this can
    // fire once with it still null on mount for eskimi_admin/adops — skip
    // resolving (and critically, do NOT treat this as an invalid selection)
    // until the real company arrives and the effect re-fires. Without this
    // guard a persisted/deep-linked company-owned blueprint 404s against
    // canUserReadPrompt (no companyId yet) and gets wrongly cleared before
    // the id ever resolves — same race class as the old loadPrompts guard
    // (bd deepcontext-jvwh.1).
    if (requiresCompanySelection && !selectedCompanyId) {
      return;
    }

    const requestId = ++selectedPromptRequestId.current;
    const companyIdToUse = requiresCompanySelection ? selectedCompanyId || undefined : undefined;

    promptApi.getById(promptFilter, companyIdToUse)
      .then((prompt) => {
        if (selectedPromptRequestId.current !== requestId) return;
        setSelectedPrompt(prompt);
      })
      .catch((err) => {
        if (selectedPromptRequestId.current !== requestId) return;
        console.error('Failed to resolve selected blueprint:', err);
        setSelectedPrompt(null);
        handleInvalidSelection();
      });
  }, [promptFilter, selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill promptDetailsCache for any row whose promptId isn't (yet)
  // covered by `selectedPrompt` — see the state comment above. Dedupes by
  // id across renders via requestedPromptIds so switching pages/filters
  // doesn't re-fetch a blueprint already in (or already requested into)
  // the cache.
  useEffect(() => {
    const idsToFetch = new Set<string>();
    analyses.forEach((a) => {
      // Skip ids selectedPrompt already covers — getPromptName/getPromptType
      // check selectedPrompt first, so fetching it again here would just be
      // a redundant duplicate request.
      if (
        a.promptId &&
        a.promptId !== selectedPrompt?.id &&
        !promptDetailsCache[a.promptId] &&
        !requestedPromptIds.current.has(a.promptId)
      ) {
        idsToFetch.add(a.promptId);
      }
    });
    if (idsToFetch.size === 0) return;

    const requiresCompanySelection = user?.role === 'eskimi_admin' || user?.role === 'adops';
    if (requiresCompanySelection && !selectedCompanyId) {
      // Same async-company-resolution guard as the selectedPrompt effect —
      // wait for the real company id instead of firing (and caching) a
      // request that will 404.
      return;
    }
    const companyIdToUse = requiresCompanySelection ? selectedCompanyId || undefined : undefined;

    idsToFetch.forEach((id) => {
      requestedPromptIds.current.add(id);
      promptApi.getById(id, companyIdToUse)
        .then((prompt) => {
          setPromptDetailsCache((prev) => ({ ...prev, [id]: prompt }));
        })
        .catch((err) => {
          console.error(`Failed to resolve blueprint ${id} for row display:`, err);
          // Leave uncached — getPromptName/getPromptType fall back to
          // their defaults; allow a retry on a future analyses update by
          // un-marking it as requested.
          requestedPromptIds.current.delete(id);
        });
    });
  }, [analyses, selectedPrompt, selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Cleanup function to cancel in-flight requests on unmount
    return () => {
      if (summaryAbortController.current) {
        summaryAbortController.current.abort();
      }
      if (analysesAbortController.current) {
        analysesAbortController.current.abort();
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Track if initial load from URL has been done to prevent double-loading
  const initialLoadDone = useRef(false);
  const lastLoadedPromptId = useRef<string | null>(null);

  // Track last loaded key to prevent duplicate API calls (React StrictMode causes double-mount)
  const lastLoadedKey = useRef<string | null>(null);

  // Load data once the deep-linked/persisted blueprint has resolved by id
  // (or if URL has brand_blueprint_id)
  useEffect(() => {
    const urlPromptId = searchParams.get('brand_blueprint_id');
    // Only auto-load if there's a prompt in the URL, it resolved successfully,
    // and we haven't loaded it yet.
    if (urlPromptId && selectedPrompt?.id === urlPromptId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      lastLoadedPromptId.current = urlPromptId;
      loadData();
    }
  }, [selectedPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentPage > 1) {
      setIsLoading(true);
      loadAnalyses(currentPage).finally(() => setIsLoading(false));
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload data when promptFilter changes (blueprint selection)
  useEffect(() => {
    if (promptFilter) {
      // Update URL with brand_blueprint_id
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('brand_blueprint_id', promptFilter);
      setSearchParams(newSearchParams, { replace: true });

      // Skip loading if this prompt was just loaded by loadData() (initial URL load)
      if (lastLoadedPromptId.current === promptFilter) {
        lastLoadedPromptId.current = null; // Reset for future changes
        return;
      }

      // Skip loading if we already loaded this prompt (prevents React StrictMode double-call)
      const loadKey = `${promptFilter}-${filter}-change`;
      if (lastLoadedKey.current === loadKey || lastLoadedKey.current === `${promptFilter}-${filter}-initial`) {
        console.log('📦 Skipping duplicate promptFilter useEffect for:', loadKey);
        return;
      }

      // Clear the result cache when prompt changes to ensure fresh data
      resultCache.current.clear();

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer
      debounceTimer.current = setTimeout(() => {
        // Double-check the key hasn't been set in the meantime
        if (lastLoadedKey.current === loadKey || lastLoadedKey.current === `${promptFilter}-${filter}-initial`) {
          return;
        }
        lastLoadedKey.current = loadKey;

        setCurrentPage(1);
        loadAnalyses(1, true); // showFullLoader = true to load counts
        // Load analysis summary for all authenticated users
        loadAnalysisSummary(promptFilter);
        // Load daily avails asynchronously
        loadDailyAvails(promptFilter);
        // Load bidder sync status asynchronously
        loadBidderSyncStatus(promptFilter);
      }, 300); // 300ms debounce
    } else {
      // Remove brand_blueprint_id from URL if no prompt is selected
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('brand_blueprint_id');
      setSearchParams(newSearchParams, { replace: true });
    }

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [promptFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch per-market result counts for the Market pill whenever the selected
  // blueprint changes (bd deepcontext-z5mx.2). `getMarkets` is optional so
  // older jest mocks of `analysisApi` (built before this pill existed) keep
  // resolving to "no markets" — i.e. the pill stays hidden — instead of
  // throwing.
  useEffect(() => {
    if (marketsAbortController.current) {
      marketsAbortController.current.abort();
    }
    if (!promptFilter) {
      setMarkets([]);
      return;
    }
    marketsAbortController.current = new AbortController();
    const signal = marketsAbortController.current.signal;
    analysisApi.getMarkets?.(promptFilter, signal, scopeCompanyId)
      .then((response) => {
        if (signal.aborted) return;
        setMarkets(response?.markets || []);
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError' || signal.aborted) return;
        console.error('Failed to load analysis markets:', err);
        setMarkets([]);
      });
  }, [promptFilter, scopeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A previously-chosen market can stop existing for the current blueprint
  // (switched blueprint, or the run that produced it aged out) — fall back
  // to "All markets" rather than silently filtering on a dead key.
  useEffect(() => {
    if (marketFilter && markets.length > 0 && !markets.some((m) => m.market === marketFilter)) {
      setMarketFilter('');
    }
  }, [markets]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload the table/tabs/KPI tiles when the Market pill selection changes,
  // and persist it in the URL (`market=`) alongside brand_blueprint_id so a
  // shared link carries the same narrowed view (bd deepcontext-z5mx.2). Skips
  // its reload on the mount-time run — the URL-seeded value is already
  // covered by the initial loadData()/promptFilter load in the same render,
  // so reloading here too would double-fire the first fetch.
  useEffect(() => {
    if (isInitialMarketEffect.current) {
      isInitialMarketEffect.current = false;
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    if (marketFilter) {
      newSearchParams.set('market', marketFilter);
    } else {
      newSearchParams.delete('market');
    }
    setSearchParams(newSearchParams, { replace: true });

    if (promptFilter) {
      setCurrentPage(1);
      setIsLoading(true);
      // updateCounts: true — a market change is a full reload of the
      // screen state, so the surviving response must repaint the tab
      // counts alongside the rows, not just the rows (bd deepcontext-s1pr.1).
      loadAnalyses(1, false, { updateCounts: true }).finally(() => setIsLoading(false));
      loadAnalysisSummary(promptFilter);
    }
  }, [marketFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload visible results when filter changes (Match/Unmatched/All)
  useEffect(() => {
    if (promptFilter) {
      setCurrentPage(1);
      setIsLoading(true);
      // updateCounts: true — switching tabs is a full reload of the screen
      // state, so the surviving response must repaint the tab counts
      // alongside the rows; otherwise a stale/racing arrival response's
      // counts can be left on screen next to an empty table
      // (bd deepcontext-s1pr.1).
      loadAnalyses(1, false, { updateCounts: true }).finally(() => setIsLoading(false));
    }
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the Export dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAnalysisSummary = async (overridePromptId?: string) => {
    if (!isAuthenticated) {
      setAnalysisSummary(null);
      return;
    }

    // Cancel any in-flight summary request
    if (summaryAbortController.current) {
      console.log('📊 Cancelling previous summary request');
      summaryAbortController.current.abort();
    }

    // Create new AbortController for this request
    summaryAbortController.current = new AbortController();
    const signal = summaryAbortController.current.signal;

    setAnalysisSummaryLoading(true);
    try {
      // Use override if provided (for initial load), otherwise use current filter
      const filterPromptId = overridePromptId !== undefined ? overridePromptId : (promptFilter || undefined);
      console.log(`📈 Loading analysis summary for prompt: ${filterPromptId || 'all'}`);

      // Use the getSummary API (no longer includes Daily Avails)
      const response = await analysisApi.getSummary(filterPromptId, signal, scopeCompanyId, marketFilter || undefined);

      // Check if the request was aborted
      if (signal.aborted) {
        console.log('📊 Summary request was aborted');
        return;
      }

      if (response.success && response.summary) {
        setAnalysisSummary(response.summary);
        console.log(`📈 Loaded analysis summary:`, response.summary);
      } else {
        setAnalysisSummary({
          totalUrls: 0,
          matchedUrls: 0,
          unmatchedUrls: 0,
          matchRate: 0,
          averageConfidence: 0
        });
      }
    } catch (err: any) {
      // Don't log or update state if the request was aborted
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('📊 Summary request was cancelled');
        return;
      }
      console.error('Failed to load analysis summary:', err);
      setAnalysisSummary(null);
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!signal.aborted) {
        setAnalysisSummaryLoading(false);
      }
    }
  };

  const loadDailyAvails = async (promptId: string) => {
    if (!isAuthenticated || !promptId) {
      setDailyAvails(null);
      setDailyAvailsUnavailable(false);
      return;
    }

    setDailyAvailsLoading(true);
    try {
      console.log(`📈 Loading daily avails for prompt: ${promptId}`);
      const response = await analysisApi.getDailyAvails(promptId);

      if (response.success && response.dailyAvails !== null && response.dailyAvails !== undefined) {
        setDailyAvails(response.dailyAvails);
        setDailyAvailsUnavailable(false);
        console.log(`📈 Loaded daily avails: ${response.dailyAvails}`);
      } else {
        // No computed value (stale/never-run refresh) — surface as
        // unavailable, not a silent 0 someone reads as "no inventory".
        setDailyAvails(null);
        setDailyAvailsUnavailable(true);
      }
    } catch (err: any) {
      console.error('Failed to load daily avails:', err);
      setDailyAvails(null);
      setDailyAvailsUnavailable(true);
    } finally {
      setDailyAvailsLoading(false);
    }
  };

  const loadBidderSyncStatus = async (promptId: string) => {
    if (!isAuthenticated || !promptId) {
      setBidderSyncStatus(null);
      return;
    }

    setBidderSyncLoading(true);
    try {
      console.log(`🔄 Loading bidder sync status for prompt: ${promptId}`);
      const response = await analysisApi.getBidderSyncStatus(promptId);

      if (response.success) {
        setBidderSyncStatus({
          syncedCount: response.syncedCount,
          notSyncedCount: response.notSyncedCount,
          failedCount: response.failedCount,
          totalMatchedUrls: response.totalMatchedUrls,
          syncPercentage: response.syncPercentage
        });
        console.log(`🔄 Loaded bidder sync status: ${response.syncedCount} synced, ${response.notSyncedCount} not synced, ${response.failedCount} failed`);
      } else {
        setBidderSyncStatus(null);
      }
    } catch (err: any) {
      console.error('Failed to load bidder sync status:', err);
      setBidderSyncStatus(null);
    } finally {
      setBidderSyncLoading(false);
    }
  };

  const loadBatchDailyAvails = async (results: AnalysisResult[]) => {
    if (!isAuthenticated || results.length === 0) {
      return;
    }

    // Prepare items for batch request
    const items = results
      .filter(r => r.urlHash) // Only include items with url hash
      .map(r => ({
        url_hash: r.urlHash!,
        is_brand_safe: r.isBrandSafe
      }));

    if (items.length === 0) {
      return;
    }

    setUrlDailyAvailsLoading(true);
    try {
      console.log(`📈 Loading batch daily avails for ${items.length} URLs`);
      const response = await analysisApi.getBatchDailyAvails(items);

      if (response.success) {
        setUrlDailyAvails(response.results);
        console.log(`📈 Loaded daily avails for ${Object.keys(response.results).length} URLs`);
      }
    } catch (err: any) {
      console.error('Failed to load batch daily avails:', err);
    } finally {
      setUrlDailyAvailsLoading(false);
    }
  };

  const loadData = async () => {
    // Check if there's a brand_blueprint_id in the URL
    const urlPromptId = searchParams.get('brand_blueprint_id');
    let selectedPromptId = promptFilter;

    if (urlPromptId && selectedPrompt?.id === urlPromptId) {
      // Use the prompt ID from URL now that getById has resolved it
      // (invoked by loadData()'s caller once selectedPrompt matches).
      selectedPromptId = urlPromptId;
    }

    // Prevent duplicate API calls (React StrictMode causes double-mount)
    const loadKey = `${selectedPromptId}-${filter}-initial`;
    if (lastLoadedKey.current === loadKey) {
      console.log('📦 Skipping duplicate loadData call for:', loadKey);
      return;
    }

    try {
      setIsLoading(true);

      if (selectedPromptId) {
        // Update promptFilter if URL has a different value, persisting the
        // deep-linked selection so it survives subsequent navigation.
        if (selectedPromptId !== promptFilter) {
          handlePromptFilterChange(selectedPromptId);
        }
      }
      // Note: Don't auto-select first prompt - require explicit user selection

      // Only load analyses if we have a selected prompt
      if (selectedPromptId) {
        // Mark as loaded before making API calls
        lastLoadedKey.current = loadKey;

        // Route the arrival load through loadAnalyses instead of issuing a
        // second, independent (unabortable) history request: that used to
        // race the [filter]/[marketFilter] effects' own loadAnalyses calls,
        // and being the only path that ever wrote counts, it could leave
        // stale non-zero counts on screen next to an empty/superseded table
        // (bd deepcontext-s1pr.1). promptIdOverride is needed because
        // handlePromptFilterChange's setPromptFilter() above hasn't
        // committed yet — loadAnalyses's own `promptFilter` closure would
        // still read the pre-arrival value.
        await loadAnalyses(1, true, { updateCounts: true, promptIdOverride: selectedPromptId });

        // Load analysis summary for all authenticated users - pass the selected prompt ID
        await loadAnalysisSummary(selectedPromptId);
        // Load daily avails asynchronously
        loadDailyAvails(selectedPromptId);
        // Load bidder sync status asynchronously
        loadBidderSyncStatus(selectedPromptId);
      }
    } catch (err: any) {
      console.error('Failed to load analysis history:', err);
      const errorMessage = formatApiErrorMessage(err, 'Failed to load analysis history');
      setError(`Failed to load analysis history: ${errorMessage}`);
      // Reset the load key on error so user can retry
      lastLoadedKey.current = null;
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnalyses = async (
    page: number,
    showFullLoader = false,
    options?: { updateCounts?: boolean; promptIdOverride?: string }
  ) => {
    // Cancel any in-flight analyses request before starting a new one so a
    // stale response (e.g. blueprint A, or a superseded tab/market/arrival
    // fetch for the same blueprint) can't overwrite the latest selection
    // after the user moves on. This is the single entry point that ever
    // calls analysisApi.getHistory — loadData() routes through it too — so
    // the arrival load and a tab/market/page change can never both have a
    // history request in flight at once (bd deepcontext-5jt, bd
    // deepcontext-s1pr.1).
    if (analysesAbortController.current) {
      console.log('📊 Cancelling previous loadAnalyses request');
      analysesAbortController.current.abort();
    }
    analysesAbortController.current = new AbortController();
    const signal = analysesAbortController.current.signal;

    // Counts are only meaningful for a full reload (filter/market/arrival
    // change) — plain pagination (page>1) keeps the existing counts unless
    // the caller opts in explicitly. Decoupled from showFullLoader so a
    // tab/market change can refresh counts without also flipping the
    // full-page spinner.
    const updateCounts = options?.updateCounts ?? showFullLoader;
    const effectivePromptId = options?.promptIdOverride ?? promptFilter;

    try {
      if (showFullLoader) {
        setIsLoading(true);
      }

      // Build filters based on current filter state - prompt is always required now
      const filters: any = {};
      if (effectivePromptId) {
        filters.promptId = effectivePromptId;
      }
      if (filter === 'safe') {
        filters.isBrandSafe = true;
      } else if (filter === 'unsafe') {
        filters.isBrandSafe = false;
      }
      if (marketFilter) {
        filters.market = marketFilter;
      }

      // Check cache first
      const cacheKey = `${effectivePromptId}-${filter}-${marketFilter}-${page}`;
      const cached = resultCache.current.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(`📦 Using cached results for ${cacheKey}`);
        if (signal.aborted) {
          return;
        }
        setAnalyses(cached.data);
        setPagination(cached.pagination);
        if (updateCounts) {
          setCounts(cached.counts);
        }
        setDataSource((cached as any).dataSource || 'mysql');
        setSelectedAnalysis(null);
        if (showFullLoader) {
          setIsLoading(false);
        }
        return;
      }

      const response = await withRetry(
        () => analysisApi.getHistory(page, 10, Object.keys(filters).length > 0 ? filters : undefined, signal, scopeCompanyId),
        3, // max retries
        3000 // 3 second base delay
      );

      // Bail out if this request was superseded while in-flight — don't
      // cache or render its results.
      if (signal.aborted) {
        console.log('📊 loadAnalyses response ignored — request was cancelled');
        return;
      }

      // Cache the results
      resultCache.current.set(cacheKey, {
        data: response.results,
        pagination: response.pagination,
        // Only cache counts when doing a full reload (to avoid overwriting static counts)
        counts: updateCounts ? (response.counts || null) : counts,
        timestamp: Date.now(),
        dataSource: (response as any).dataSource || 'mysql'
      });

      setAnalyses(response.results);
      setPagination(response.pagination);
      // Only update counts when doing a full reload (filter/market/arrival
      // change), not on plain pagination — but always alongside the rows
      // from the same response, never from a different in-flight request.
      if (updateCounts) {
        setCounts(response.counts || null);
      }
      setDataSource((response as any).dataSource || 'mysql');
      setSelectedAnalysis(null); // Clear selection when changing pages

      // Load daily avails for visible URLs asynchronously
      loadBatchDailyAvails(response.results);
    } catch (err: any) {
      // Don't surface or react to aborted requests — the caller already
      // moved on to a newer selection.
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || signal.aborted) {
        console.log('📊 loadAnalyses request was cancelled');
        return;
      }
      console.error('Failed to load analysis history:', err);
      const errorMessage = formatApiErrorMessage(err, 'Failed to load analysis history');
      setError(`Failed to load analysis history: ${errorMessage}`);
    } finally {
      if (showFullLoader && !signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchAnalysisDetails = async (analysisId: string) => {
    try {
      const detailsResponse = await analysisApi.getById(analysisId);
      // Merge with existing selection to preserve any data that might be
      // missing from API — but only if this response still belongs to the
      // currently selected row (the modal may have closed or moved on to a
      // different row while this request was in flight — bd deepcontext-wmmh).
      setSelectedAnalysis(prev =>
        prev && prev.id === analysisId ? { ...prev, ...detailsResponse } : prev
      );
    } catch (error) {
      console.error('Failed to fetch analysis details:', error);
      // Fall back to the basic data - don't overwrite if API fails
    }
  };

  // Resolve a row's own blueprint by id rather than assuming it always
  // matches `selectedPrompt` — a stale row from the previous selection can
  // render for a beat during a blueprint switch, and the no-filter ("All
  // blueprints") state can show rows spanning many blueprints. Falls back
  // to promptDetailsCache (populated lazily, never a capped preload —
  // bd deepcontext-q2bz.1).
  const getPromptName = (promptId: string) => {
    if (selectedPrompt?.id === promptId) return selectedPrompt.name;
    return promptDetailsCache[promptId]?.name || 'Unknown Prompt';
  };

  const getPromptType = (promptId: string): 'brand-safety' | 'audience-targeting' => {
    if (selectedPrompt?.id === promptId) return selectedPrompt.type;
    return promptDetailsCache[promptId]?.type || 'brand-safety';
  };

  const getAnalysisTypeLabel = (analysis: AnalysisResult) => {
    const promptType = getPromptType(analysis.promptId);
    if (promptType === 'audience-targeting') {
      return {
        positive: 'Match',
        negative: 'No Match',
        positiveDescription: 'Audience Match',
        negativeDescription: 'Not Audience Match'
      };
    } else {
      return {
        positive: 'Match',
        negative: 'No Match',
        positiveDescription: 'Match',
        negativeDescription: 'No Match'
      };
    }
  };

  // No client-side filtering needed - filtering is done server-side
  const filteredAnalyses = analyses;

  const formatUrl = (url: string) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const urlObj = new URL(decodedUrl);
      return urlObj.hostname;
    } catch {
      try {
        return decodeURIComponent(url);
      } catch {
        return url;
      }
    }
  };

  const canExportPrompt = Boolean(promptFilter) && Boolean(analysisSummary) && (analysisSummary?.totalUrls || 0) > 0;
  const canExportSelected = Boolean(selectedAnalysis?.bulkJobId) && user?.role === 'admin';

  // Table-fill contract (bd-n3uyh, shared TableFillBox — deepcontext-wbra):
  // a full page fills the table box down to the pagination and scrolls
  // internally; a sparse page hugs its content. Keyed off the TOTAL result
  // count vs page size (mirrors usePromptList.ts's paginationInfo.totalCount
  // check) — NOT the current page's row count, which would wrongly hug on
  // the tail page of a multi-page list (e.g. 25 results/10 per page: page 3
  // returns only 5 rows, but the list is still multi-page overall).
  const isFullPage = Boolean(pagination) && pagination!.totalCount >= pagination!.limit;

  // Blueprint field: captioned, first on the shared header row, mounted into
  // AppLayout's left context slot (deepcontext-tpe1).
  const blueprintField = (
    <div className="w-56 flex-shrink-0">
      <AnalysisProfileSelect
        value={promptFilter}
        onChange={handlePromptFilterChange}
        placeholder="All blueprints"
        className="w-full"
        size="md"
        disabled={isLoading}
        onInvalidSelection={handleInvalidSelection}
        captioned
      />
    </div>
  );

  // Market pill: next to Blueprint in the same left context slot, hidden
  // until a blueprint is selected and its runs' markets are known
  // (bd deepcontext-z5mx.2).
  const marketField = (
    <MarketPill
      markets={markets}
      selectedMarket={marketFilter}
      onSelectMarket={setMarketFilter}
    />
  );

  // Actions row: segmented switch + export. Portaled into AppLayout's shared
  // app-page-actions slot (deepcontext-6i0j.5) instead of rendering in the
  // page body, mirroring ScheduledJobsPage.tsx.
  const actionsRow = (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className={isLoading ? 'opacity-60 pointer-events-none' : ''}>
          <Tabs
            value={filter}
            onChange={(value) => setFilter(value as 'all' | 'safe' | 'unsafe')}
            tabs={[
              { value: 'all', label: `All · ${(counts?.total ?? 0).toLocaleString()}` },
              { value: 'safe', label: `Matched · ${(counts?.safe ?? 0).toLocaleString()}` },
              { value: 'unsafe', label: `Unmatched · ${(counts?.unsafe ?? 0).toLocaleString()}` },
            ]}
          />
        </div>
      </div>

      <div className="relative flex-shrink-0" ref={exportMenuRef}>
        <Button variant="outline" onClick={() => setExportMenuOpen((open) => !open)}>
          Export
          <svg className={`w-3.5 h-3.5 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>

        {/* Always mounted (visibility toggled via CSS, not conditional
            rendering) so PromptExport/BulkJobExport — and the dialogs
            they own — survive the menu closing the instant a menu item
            is clicked (onMenuAction closes the menu in the same tick
            that the child opens its own dialog). */}
        <div
          data-testid="export-menu"
          className={`absolute right-0 mt-2 w-64 rounded-xl border border-line bg-surface shadow-lg z-20 py-1 overflow-hidden ${exportMenuOpen ? '' : 'hidden'}`}
        >
          {canExportPrompt && (
            <PromptExport
              renderAs="menu-items"
              onMenuAction={() => setExportMenuOpen(false)}
              promptId={promptFilter}
              promptName={getPromptName(promptFilter)}
              prompt={selectedPrompt}
              totalUrls={analysisSummary!.totalUrls}
              matchedUrls={analysisSummary!.matchedUrls}
              unmatchedUrls={analysisSummary!.unmatchedUrls}
              averageConfidence={analysisSummary!.averageConfidence}
              dailyAvails={dailyAvails || 0}
            />
          )}
          {canExportSelected && (
            <BulkJobExport
              renderAs="menu-items"
              onMenuAction={() => setExportMenuOpen(false)}
              bulkJobId={selectedAnalysis!.bulkJobId!}
              promptName={getPromptName(selectedAnalysis!.promptId)}
            />
          )}
          {!canExportPrompt && !canExportSelected && (
            <div className="px-4 py-3 text-sm text-ink-muted">No export available</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 gap-5">
      {contextSlot && createPortal(
        <>
          {blueprintField}
          {marketField}
        </>,
        contextSlot
      )}
      {actionsSlot && createPortal(actionsRow, actionsSlot)}

      {/* Error Display */}
      {error && (
        <div className="flex-shrink-0 bg-risk-bg border border-risk rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-risk" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-risk">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-risk underline text-sm mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI card row — standard 5-card summary, never an inline metric strip */}
      {isAuthenticated && (
        analysisSummaryLoading ? (
          <div className="flex-shrink-0 bg-surface rounded-lg shadow-sm border border-line p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        ) : analysisSummary && analysisSummary.totalUrls > 0 ? (
          <div className="flex-shrink-0 grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard
              testId="kpi-card-total"
              value={formatCompactNumber(analysisSummary.totalUrls)}
              label="Total Scanned URLs"
            />
            <StatCard
              testId="kpi-card-matches"
              value={formatCompactNumber(analysisSummary.matchedUrls)}
              label="Total Matches"
              valueColorClassName="text-safe"
            />
            <StatCard
              testId="kpi-card-rate"
              value={`${Math.round(analysisSummary.matchRate * 100)}%`}
              label="Match Rate"
            />
            <StatCard
              testId="kpi-card-confidence"
              value={`${(analysisSummary.averageConfidence * 100).toFixed(1)}%`}
              label="Average Confidence"
            />
            <div data-testid="kpi-card-avails" className="bg-surface rounded-xl shadow-sm border border-line p-4">
              {dailyAvailsLoading ? (
                <div className="text-2xl font-extrabold text-accent-active">
                  <span className="inline-block w-5 h-5 border-2 border-accent-active border-t-transparent rounded-full animate-spin" />
                </div>
              ) : dailyAvailsUnavailable ? (
                <div className="text-2xl font-extrabold text-ink-muted opacity-45">&mdash;</div>
              ) : dailyAvails !== null ? (
                <div className="text-2xl font-extrabold text-accent-active" title={dailyAvails.toLocaleString()}>
                  {formatCompactNumber(dailyAvails)}
                </div>
              ) : (
                <div className="text-2xl font-extrabold text-accent-active">-</div>
              )}
              {dailyAvailsUnavailable && (
                <div className="text-xs text-ink-muted mt-0.5">
                  Avails unavailable — refresh failed
                </div>
              )}
              <div className="text-xs font-semibold text-ink-muted mt-1 flex items-center gap-1">
                Daily Avails
                <span className="relative inline-block group">
                  <svg className="w-3.5 h-3.5 text-ink-muted hover:text-ink cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-canvas bg-ink rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-normal pointer-events-none z-10 min-w-[200px] text-left">
                    {bidderSyncLoading ? (
                      'Checking bidder sync...'
                    ) : bidderSyncStatus ? (
                      <span className="block">
                        <span className="block font-semibold mb-1">Bidder Sync Status</span>
                        <span className="block text-safe">{bidderSyncStatus.syncedCount} synced ({bidderSyncStatus.syncPercentage}%)</span>
                        <span className="block text-warn">{bidderSyncStatus.notSyncedCount} not synced</span>
                        {bidderSyncStatus.failedCount > 0 && (
                          <span className="block text-risk">{bidderSyncStatus.failedCount} failed to check</span>
                        )}
                      </span>
                    ) : (
                      'Bidder sync status unavailable'
                    )}
                  </span>
                </span>
              </div>
            </div>
          </div>
        ) : null
      )}

      {/* Results table — the row fills the remaining page height (bd-n3uyh,
          deepcontext-wbra) so the table box below can reach the table-fill
          contract. Spans the full content width (deepcontext-6i0j.5); row
          details now open in a modal instead of an always-mounted side
          rail. */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Standard card chrome (rounded-2xl border-line bg-surface
            shadow-sm) wraps both the table and the pagination footer,
            matching the shared table-card spec (deepcontext-c0p7.5). The
            card itself stays mounted across loading/empty/table states so
            the pagination footer never disappears mid-page-turn. */}
        <div
          data-testid="audit-results-panel"
          className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm"
        >
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
                <p className="text-ink-muted mt-4">Loading analyses...</p>
              </div>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <p className="text-ink-muted">
                No analyses found matching your filters.
                {filter !== 'all' && (
                  <span className="block mt-2 text-sm">
                    Try selecting "All" results.
                  </span>
                )}
              </p>
            </div>
          ) : (
              <TableFillBox isFullPage={isFullPage} data-testid="results-table-fill-box">
                <Table className={`${tableFillHeightClass(isFullPage)} table-fixed`}>
                  <TableHead>
                    <tr>
                      <TableHeaderCell className="w-[40%]">URL</TableHeaderCell>
                      <TableHeaderCell className="w-[16%]">Analyzed</TableHeaderCell>
                      <TableHeaderCell className="w-[16%]">Agreement</TableHeaderCell>
                      <TableHeaderCell className="w-[16%]">Verdict</TableHeaderCell>
                      <TableHeaderCell className="w-[12%] text-right">Conf.</TableHeaderCell>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {filteredAnalyses.map((analysis) => {
                      const labels = getAnalysisTypeLabel(analysis);
                      const promptType = getPromptType(analysis.promptId);
                      const selected = selectedAnalysis?.id === analysis.id;
                      return (
                        <TableRow
                          key={analysis.id}
                          data-testid={`result-row-${analysis.id}`}
                          onClick={() => {
                            setSelectedAnalysis(analysis);
                            fetchAnalysisDetails(analysis.id);
                          }}
                          className={`cursor-pointer ${selected ? 'bg-nav-active' : ''}`}
                        >
                          <TableCell
                            style={selected ? { boxShadow: 'inset 3px 0 0 var(--accent)' } : undefined}
                          >
                            <span className="font-bold text-accent-active block truncate max-w-[220px]">
                              {formatUrl(analysis.url)}
                            </span>
                            <span className="text-xs text-ink-muted">{getPromptName(analysis.promptId)}</span>
                          </TableCell>
                          <TableCell className="text-ink-muted">
                            {new Date(analysis.analyzedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell>
                            <AgreementPill agreement={getAgreement(analysis, promptType)} />
                          </TableCell>
                          <TableCell>
                            <VerdictPill isBrandSafe={analysis.isBrandSafe} label={analysis.isBrandSafe ? labels.positive : labels.negative} />
                          </TableCell>
                          <TableCell className="text-right font-extrabold text-ink">
                            {(analysis.confidence * 100).toFixed(0)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableFillBox>
            )}

            {/* Pagination — standard in-card ghost footer (deepcontext-c0p7.5),
                a sibling of the table/loading/empty states so it stays put
                across page turns instead of disappearing during a load. */}
            {pagination && pagination.totalPages > 1 && (
              <div
                data-testid="audit-pagination"
                className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5"
              >
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </div>
            )}
        </div>
      </div>

      {/* Analysis details modal — replaces the old always-mounted side rail
          so the results table can span the full content width
          (deepcontext-6i0j.5). Reuses the app's standard Modal chrome, same
          as the export dialogs on this page (PromptExport/BulkJobExport). */}
      <Modal
        open={Boolean(selectedAnalysis)}
        onClose={() => setSelectedAnalysis(null)}
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        {selectedAnalysis && (
            <div data-testid="detail-rail" className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-ink">Analysis Details</h3>
                {(() => {
                  const labels = getAnalysisTypeLabel(selectedAnalysis);
                  return (
                    <VerdictPill
                      isBrandSafe={selectedAnalysis.isBrandSafe}
                      label={selectedAnalysis.isBrandSafe ? labels.positiveDescription : labels.negativeDescription}
                      size="md"
                    />
                  );
                })()}
                <div className="ml-auto flex items-center gap-2">
                  {selectedAnalysis.bulkJobId && user?.role === 'admin' && (
                    <BulkJobExport
                      renderAs="icon"
                      bulkJobId={selectedAnalysis.bulkJobId}
                      promptName={getPromptName(selectedAnalysis.promptId)}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">URL</label>
                <a
                  href={selectedAnalysis.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-accent-active hover:opacity-80 break-all"
                >
                  {decodeURIComponent(selectedAnalysis.url)}
                </a>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">Prompt Used</label>
                <p className="text-sm text-ink-muted">{getPromptName(selectedAnalysis.promptId)}</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">Confidence Level</label>
                <Progress value={selectedAnalysis.confidence * 100} className="h-1.5" />
                <span className="block mt-1 text-xs font-bold text-ink">
                  {(selectedAnalysis.confidence * 100).toFixed(1)}%
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">Analysis Reasoning</label>
                <p className="text-sm text-ink-muted">
                  {(() => {
                    // Clean up parsing error messages that contain raw JSON
                    if (selectedAnalysis.reasoning.startsWith('Parsing error:')) {
                      // Extract just the error type, removing raw JSON response
                      const cleanError = selectedAnalysis.reasoning.split('. Raw response:')[0];
                      return cleanError || 'AI response parsing failed. Please try analyzing this URL again.';
                    }
                    return selectedAnalysis.reasoning;
                  })()}
                </p>

                {/* Content Summary */}
                <div className="mt-3">
                  <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted mb-1.5">Content Summary</label>
                  <p className="text-xs text-ink-muted">{selectedAnalysis.contentSummary}</p>
                </div>
              </div>

              {/* Keyword Analysis — canvas-inset box. Only if a keyword list was selected (keyword blocking ran). */}
              {selectedAnalysis.keywordBlocking &&
               selectedAnalysis.keywordBlocking.reasoning !== NO_KEYWORD_LIST_REASONING && (
                <div data-testid="keyword-box" className="bg-canvas rounded-xl p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink-muted">Keyword Result:</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                      selectedAnalysis.keywordBlocking.isBlocked
                        ? 'bg-risk-bg text-risk'
                        : 'bg-ghost-bg text-ink-muted'
                    }`}>
                      {selectedAnalysis.keywordBlocking.isBlocked ? 'Blocked' : 'Allowed'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink-muted">AI vs Keywords:</span>
                    <AgreementPill agreement={getAgreement(selectedAnalysis, getPromptType(selectedAnalysis.promptId))} />
                  </div>

                  {selectedAnalysis.keywordBlocking.matchedKeywords && Array.isArray(selectedAnalysis.keywordBlocking.matchedKeywords) && selectedAnalysis.keywordBlocking.matchedKeywords.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-ink-muted">Matched Keywords ({selectedAnalysis.keywordBlocking.totalMatches}):</span>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {selectedAnalysis.keywordBlocking.matchedKeywords.slice(0, 5).map((keyword, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-ghost-bg text-ink">
                            {keyword}
                          </span>
                        ))}
                        {selectedAnalysis.keywordBlocking.matchedKeywords.length > 5 && (
                          <span className="text-xs text-ink-muted self-center">
                            +{selectedAnalysis.keywordBlocking.matchedKeywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="text-xs font-semibold text-ink-muted">Keyword Reasoning:</span>
                    <p className="text-xs text-ink-muted mt-1">{selectedAnalysis.keywordBlocking.reasoning}</p>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-line space-y-2">
                <div className="flex justify-between text-xs text-ink-muted">
                  <span>Analyzed</span>
                  <span className="font-bold text-ink">{new Date(selectedAnalysis.analyzedAt).toLocaleString()}</span>
                </div>
                {selectedAnalysis.urlHash && (
                  urlDailyAvails[selectedAnalysis.urlHash] ? (
                    <div className="flex justify-between text-xs text-accent-active">
                      <span>Daily Avails</span>
                      <span className="font-bold">{formatCompactNumber(urlDailyAvails[selectedAnalysis.urlHash])}</span>
                    </div>
                  ) : urlDailyAvailsLoading ? (
                    <div className="flex justify-between text-xs text-accent-active">
                      <span>Daily Avails</span>
                      <span className="inline-block w-3 h-3 border border-accent-active border-t-transparent rounded-full animate-spin"></span>
                    </div>
                  ) : null
                )}
                {selectedAnalysis.actualCost != null && selectedAnalysis.actualCost > 0 && (
                  <div className="flex justify-between text-xs text-ink-muted">
                    <span>Cost</span>
                    <span className="font-bold text-ink">${selectedAnalysis.actualCost.toFixed(4)}</span>
                  </div>
                )}
                {(selectedAnalysis.dailyRequests ?? 0) > 0 && (
                  <div className="flex justify-between text-xs text-accent-active">
                    <span>Daily Requests</span>
                    <span className="font-bold">{(selectedAnalysis.dailyRequests ?? 0).toLocaleString()}</span>
                  </div>
                )}

                <div className="pt-2">
                  <FeedbackButtons
                    analysis={selectedAnalysis}
                    onFeedbackSubmitted={(id, agree, reasoning) => {
                      // Clear the cache to ensure fresh data on next fetch
                      resultCache.current.clear();

                      // If user disagreed, we need to handle the status change
                      if (!agree) {
                        // Find the current item first (outside of setAnalyses to avoid issues)
                        const currentItem = analyses.find(a => a.id === id);
                        if (!currentItem) return;

                        const newBrandSafeStatus = !currentItem.isBrandSafe;

                        // Check if the item should be removed from current filter view
                        const shouldRemoveFromList =
                          (filter === 'safe' && !newBrandSafeStatus) ||  // Was in Matched, now Unmatched
                          (filter === 'unsafe' && newBrandSafeStatus);   // Was in Unmatched, now Matched

                        if (shouldRemoveFromList) {
                          // Clear selection since the item is no longer in the list
                          setSelectedAnalysis(null);

                          // Update counts optimistically (outside setAnalyses to avoid double-counting)
                          setCounts(prevCounts => {
                            if (!prevCounts) return prevCounts;
                            if (filter === 'safe') {
                              // Moved from safe to unsafe
                              return { ...prevCounts, safe: prevCounts.safe - 1, unsafe: prevCounts.unsafe + 1 };
                            } else {
                              // Moved from unsafe to safe
                              return { ...prevCounts, safe: prevCounts.safe + 1, unsafe: prevCounts.unsafe - 1 };
                            }
                          });

                          // Remove item from the list
                          setAnalyses(prev => prev.filter(a => a.id !== id));
                        } else {
                          // 'all' filter - update item in place
                          const feedbackText = reasoning || 'No specific reason provided';
                          const updatedReasoning = `User explicitly classified this as ${newBrandSafeStatus ? 'brand safe' : 'not brand safe'} with the following feedback: "${feedbackText}"`;

                          const updatedAnalysis = {
                            ...currentItem,
                            isBrandSafe: newBrandSafeStatus,
                            reasoning: updatedReasoning,
                            feedback: { agree, reasoning, timestamp: new Date().toISOString() }
                          };

                          setSelectedAnalysis(updatedAnalysis);
                          setAnalyses(prev => prev.map(a => a.id === id ? updatedAnalysis : a));
                        }
                      } else {
                        // User agreed - just add feedback without changing status
                        setAnalyses(prev => prev.map(a => {
                          if (a.id === id) {
                            const updatedAnalysis = {
                              ...a,
                              feedback: { agree, reasoning, timestamp: new Date().toISOString() }
                            };
                            setSelectedAnalysis(updatedAnalysis);
                            return updatedAnalysis;
                          }
                          return a;
                        }));
                      }
                    }}
                  />
                </div>
              </div>
            </div>
        )}
      </Modal>
    </div>
  );
};

export default AnalysisHistory;
